import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * A dialog surface that picks its width by NAME instead of spelling one out.
 *
 * Every dialog in the vault set its own width, so five dialogs ended up four different
 * widths — and one of them, LinkProductDialog, was wrong without anyone noticing: it wrote
 * `max-w-md` with no `sm:` prefix, so shadcn's own `sm:max-w-lg` outranked it on desktop
 * (512px, not the 448px it reads as) while on a phone it replaced the default
 * `max-w-[calc(100%-2rem)]` and ran the dialog edge to edge. A width written per call site
 * is a width nobody is comparing.
 *
 * `src/components/ui/` is read-only in this repo, so this WRAPS the shadcn primitive rather
 * than adding a variant to it. Everything else about the dialog — the overlay, the close
 * button, the open/close animation, the focus trap — is still shadcn's; the only thing added
 * here is the size.
 *
 * Three sizes, because there are three jobs:
 *
 *   form    448px  a short form or a confirmation. Move to…, Sign, Link a product.
 *   wide    672px  a dialog with a list, a drop zone or several fields at once. Bulk upload.
 *   viewer  1300px, 94vw / 92vh — reading a document, where the content is the point.
 *
 * `overflow-x-hidden` is part of `form` and `wide` on purpose: both sizes routinely hold a
 * user-supplied filename, and one 120-character name with no spaces would otherwise stretch
 * the dialog past the screen. The viewer scrolls its own content and is left alone.
 *
 * `className` still passes through for layout that is not a width — `p-0`, `flex flex-col`.
 * A width does not belong there; `uiConsistency.test.ts` fails the build if one appears.
 */
const sizes = cva('', {
  variants: {
    size: {
      form: 'overflow-x-hidden sm:max-w-md',
      wide: 'overflow-x-hidden sm:max-w-2xl',
      viewer: 'h-[92vh] w-[94vw] max-w-[1300px] sm:max-w-[1300px]',
    },
  },
  defaultVariants: { size: 'form' },
});

type Props = ComponentProps<typeof DialogContent> & VariantProps<typeof sizes>;

export function SizedDialog({ size, className, ...props }: Props) {
  return <DialogContent className={cn(sizes({ size }), className)} {...props} />;
}
